import { InstantRange } from "./scheduleApi.ts";
import { User } from "oidc-client-ts";
import { QueryObserverOptions } from "@tanstack/react-query";
import { doGet, doPost, doPut } from "./util/apiHelper.ts";
import {
  useAuthenticatedInfiniteQuery,
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import {
  BOOKING_INVALIDATION_KEYS,
  BOOKING_QUERY_KEYS,
} from "./util/queryKeys.ts";

export interface BookingCreationDto {
  teacherId: string;
  locationId: string;
  instrument: string;
  timeSlots: InstantRange[];
}

export type BookingStatus = "INITIALIZED" | "CREATED" | "ACCEPTED" | "BOOKED";

export type BookingTimeslotStatus = "BOOKED" | "CANCELLED";

export interface BookingListResponseDto {
  bookings: BookingResponseDto[];
}

export type BookingTimeslotResponseDto = {
  id: string;
  status: BookingTimeslotStatus;
} & InstantRange;

export interface BookingResponseDto {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherProfilePictureUrl: string;
  bookerId: string;
  bookerName: string;
  bookerProfilePictureUrl: string;
  locationId: string;
  locationName: string;
  instrument: string;
  timeSlots: BookingTimeslotResponseDto[];
  status: BookingStatus;
  price: number;
  createdAt?: string; // ISO instant
}

export interface BookingTimeslotCancelInformationDto {
  id: string;
  timeslot: InstantRange;
  cancellable: boolean;
  cancellableAmount: number;
  cancelledAmount?: number;
  totalAmount: number;
  status: BookingTimeslotStatus;
}

export interface BookingCancelInformationDto {
  timeslots: BookingTimeslotCancelInformationDto[];
}

export interface BookingCancellationRequestDto {
  bookingCancelInformation: BookingCancelInformationDto;
  timeSlots: string[];
  bookingId: string;
}

export interface BookingPageDto {
  bookings: BookingResponseDto[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AvailableBookingSlot {
  from: string; // ISO instant
  to: string; // ISO instant
}

export interface AvailableBookingSlotsDto {
  slots: AvailableBookingSlot[];
}

async function postBooking(
  user: User,
  bookingCreationDto: BookingCreationDto,
): Promise<BookingResponseDto> {
  return await doPost<BookingResponseDto>(
    `/users/${user.profile.sub}/bookings`,
    user,
    bookingCreationDto,
  );
}

async function acceptBooking(
  user: User,
  bookingId: string,
): Promise<BookingResponseDto> {
  return await doPut<BookingResponseDto>(
    `/teachers/${user.profile.sub}/bookings/${bookingId}`,
    user,
  );
}

const getBookings = async (user: User, path: string) => {
  return (await doGet<BookingListResponseDto>(path, user)).bookings;
};

const getBooking = async (user: User, bookingId: string) => {
  return doGet<BookingResponseDto>(
    `/users/${user.profile.sub}/bookings/${bookingId}`,
    user,
  );
};

async function getSingleUserBooking(
  user: User,
  bookingId: string,
): Promise<BookingResponseDto> {
  return await getBooking(user, bookingId);
}

async function getUserBookings(user: User): Promise<BookingResponseDto[]> {
  const bookingPath = `/users/${user.profile.sub}/bookings`;
  return await getBookings(user, bookingPath);
}

async function getTeacherBookings(user: User): Promise<BookingResponseDto[]> {
  return await getBookings(user, `/teachers/${user.profile.sub}/bookings`);
}

async function getBookingCancelInformation(
  user: User,
  bookingId: string,
): Promise<BookingCancelInformationDto> {
  return await doGet(
    `/users/${user.profile.sub}/bookings/${bookingId}/cancel-information`,
    user,
  );
}

async function getAvailableBookingSlots(
  user: User,
  teacherId: string,
  locationId: string,
  durationInMinutes: number,
): Promise<AvailableBookingSlotsDto> {
  return await doGet<AvailableBookingSlotsDto>(
    `/teachers/${teacherId}/bookings/available-slots?locationId=${locationId}&durationInMinutes=${durationInMinutes}`,
    user,
  );
}

const TEACHER_DASHBOARD_PAGE_SIZE = 10;

async function getTeacherBookingsPaged(
  user: User,
  page: number,
  sortBy: "createdAt" | "timeslot",
  sortDir: "asc" | "desc",
): Promise<BookingPageDto> {
  const params = new URLSearchParams({
    page: String(page),
    size: String(TEACHER_DASHBOARD_PAGE_SIZE),
    sortBy,
    sortDir,
  });
  return doGet<BookingPageDto>(
    `/teachers/${user.profile.sub}/bookings?${params.toString()}`,
    user,
  );
}

async function cancelBooking(
  user: User,
  bookingId: string,
  bookingCancellationRequest: BookingCancellationRequestDto,
) {
  return await doPost<void>(
    `/users/${user.profile.sub}/bookings/${bookingId}/cancellation`,
    user,
    bookingCancellationRequest,
  );
}

export function useBookingMutation() {
  return useAuthenticatedMutation(
    (bookingCreationDto: BookingCreationDto, user) => {
      return postBooking(user, bookingCreationDto);
    },
    {
      invalidateKeys: [
        BOOKING_QUERY_KEYS.userBookings(),
        BOOKING_QUERY_KEYS.teacherBookings(),
      ],
    },
  );
}

export function useAcceptBookingMutation() {
  return useAuthenticatedMutation(
    (bookingId: string, user) => {
      return acceptBooking(user, bookingId);
    },
    {
      invalidateKeys: [
        BOOKING_QUERY_KEYS.userBookings(),
        BOOKING_QUERY_KEYS.teacherBookings(),
        BOOKING_INVALIDATION_KEYS.userBooking,
        BOOKING_INVALIDATION_KEYS.teacherBookingsDashboard,
      ],
    },
  );
}

export function useUserBookingsQuery() {
  return useAuthenticatedQuery(
    BOOKING_QUERY_KEYS.userBookings(),
    (user) => {
      return getUserBookings(user);
    },
    { initialData: [] },
  );
}

export function useUserBookingQuery(
  bookingId?: string,
  options?: Omit<
    QueryObserverOptions<BookingResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  return useAuthenticatedQuery(
    BOOKING_QUERY_KEYS.userBooking(bookingId),
    (user) => {
      return getSingleUserBooking(user, bookingId!);
    },
    {
      ...options,
      enabled: !!bookingId,
    },
  );
}

export function useTeacherBookingsQuery() {
  return useAuthenticatedQuery(
    BOOKING_QUERY_KEYS.teacherBookings(),
    (user) => getTeacherBookings(user),
    { initialData: [] },
  );
}

export function useBookingCancelInformationQuery(bookingId?: string) {
  return useAuthenticatedQuery(
    ["bookingCancelInformation", bookingId],
    (user) => getBookingCancelInformation(user, bookingId!),
    {
      enabled: !!bookingId,
    },
  );
}

export function useAvailableBookingSlotsQuery(
  teacherId?: string,
  locationId?: string,
  durationInMinutes?: number,
) {
  return useAuthenticatedQuery(
    ["available-booking-slots", teacherId, locationId, durationInMinutes],
    (user) =>
      getAvailableBookingSlots(
        user,
        teacherId!,
        locationId!,
        durationInMinutes!,
      ),
    { enabled: !!teacherId && !!locationId && !!durationInMinutes },
  );
}

export function useTeacherBookingsDashboardQuery(
  sortBy: "createdAt" | "timeslot",
  sortDir: "asc" | "desc",
) {
  return useAuthenticatedInfiniteQuery<BookingPageDto>(
    ["teacher-bookings-dashboard", sortBy, sortDir],
    (user, page) => getTeacherBookingsPaged(user, page, sortBy, sortDir),
    (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  );
}

export function useBookingCancellationMutation() {
  return useAuthenticatedMutation(
    (bookingCancellationRequest: BookingCancellationRequestDto, user) => {
      return cancelBooking(
        user,
        bookingCancellationRequest.bookingId,
        bookingCancellationRequest,
      );
    },
    {
      invalidateKeys: [
        BOOKING_QUERY_KEYS.userBookings(),
        BOOKING_QUERY_KEYS.teacherBookings(),
        BOOKING_INVALIDATION_KEYS.userBooking,
        BOOKING_INVALIDATION_KEYS.cancelInformation,
      ],
    },
  );
}
